import { ObjectId } from "@novorender/api";
import { HierarcicalObjectReference } from "@novorender/data-js-api";
import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { highlightActions, useDispatchHighlighted, useLazyHighlighted } from "contexts/highlighted";
import { selectionBasketActions, useDispatchSelectionBasket } from "contexts/selectionBasket";
import { CameraType, ObjectVisibility, renderActions, selectSceneStatus } from "features/render";
import { isGlSpace } from "features/render/utils";
import { useAbortController } from "hooks/useAbortController";
import { explorerActions, selectUrlSearchQuery } from "slices/explorer";
import { AsyncStatus } from "types/misc";
import { getTotalBoundingSphere } from "utils/objectData";
import { batchedPropertySearch, searchByPatterns, searchDeepByPatterns } from "utils/search";

export function useHandleUrlSearch() {
    const {
        state: { db, scene },
    } = useExplorerGlobals();

    const highlighted = useLazyHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchSelectionBasket = useDispatchSelectionBasket();

    const dispatch = useAppDispatch();
    const query = useAppSelector(selectUrlSearchQuery);
    const sceneStatus = useAppSelector(selectSceneStatus);

    const [abortController] = useAbortController();

    useEffect(() => {
        search();

        async function search() {
            if (!query || !db || !scene || sceneStatus.status !== AsyncStatus.Success) {
                return;
            }
            const abortSignal = abortController.current.signal;
            const searchPatterns = query.pattern;
            const includeDescendants = query.options.includeDescendants;
            const loadingHandle = performance.now();
            dispatch(renderActions.addLoadingHandle(loadingHandle));

            let foundIds = [] as ObjectId[];
            let foundRefs = [] as HierarcicalObjectReference[];
            try {
                const callback = (ids: number[]) => {
                    foundIds = foundIds.concat(ids);
                    dispatchHighlighted(highlightActions.add(ids));
                };

                if (!includeDescendants) {
                    await searchByPatterns({
                        db,
                        searchPatterns,
                        abortSignal,
                        callback: (refs) => callback(refs.map((r) => r.id)),
                    });
                } else {
                    await searchDeepByPatterns({
                        db,
                        searchPatterns,
                        abortSignal,
                        callback,
                    });
                }

                if (foundIds.length) {
                    foundRefs = await batchedPropertySearch({
                        property: "id",
                        value: foundIds.map((id) => String(id)),
                        db,
                        abortSignal,
                    });
                }
            } catch (e) {
                console.warn(e);
            }

            if (foundRefs.length) {
                const boundingSphere = getTotalBoundingSphere(foundRefs, {
                    flip: isGlSpace(scene.up),
                });
                if (boundingSphere) {
                    dispatch(renderActions.setCamera({ type: CameraType.Pinhole, zoomTo: boundingSphere }));
                }
            }

            const selectionOnly = query.options.selectionOnly;

            if (selectionOnly === "1") {
                dispatch(renderActions.setDefaultVisibility(ObjectVisibility.SemiTransparent));
            } else if (selectionOnly === "2") {
                dispatch(renderActions.setDefaultVisibility(ObjectVisibility.Transparent));
            } else if (selectionOnly === "3") {
                dispatchSelectionBasket(selectionBasketActions.add(Array.from(highlighted.current.idArr)));
                dispatchHighlighted(highlightActions.setIds([]));
                dispatch(renderActions.setDefaultVisibility(ObjectVisibility.Transparent));
            } else {
                dispatch(renderActions.setDefaultVisibility(ObjectVisibility.Neutral));
            }

            dispatch(explorerActions.setUrlSearchQuery(undefined));
            dispatch(renderActions.setMainObject(foundIds[0]));
            dispatch(renderActions.removeLoadingHandle(loadingHandle));
        }
    }, [
        query,
        dispatch,
        abortController,
        db,
        dispatchHighlighted,
        scene,
        dispatchSelectionBasket,
        highlighted,
        sceneStatus,
    ]);
}
